<template>
  <div>
    <div v-if="!loading" class="project">
      <Subheader :title="project.title" class="project__subheader" />
      <ProjectProfileImage :project="project" class="project__image" />

      <div class="app-content-column">
        <div class="project__card">
          <router-link :to="{ name: 'Profile', params: { userId: project.makerRef.id } }">
            <UserProfileImage :profile-pic="project.makerRef.profilePic" />
          </router-link>

          <!-- Maker Username -->
          <router-link :to="{ name: 'Profile', params: { userId: project.makerRef.id } }">
            <div
              v-if="
                project.makerRef && project.makerRef.username && project.makerRef.username !== ''
              "
              class="project__username"
            >
              {{ project.makerRef ? project.makerRef.username : 'No Name Provided' }}
            </div>
          </router-link>

          <!-- Project Verified Status -->
          <div v-if="project.isVerified" class="project__verified-status">
            <font-awesome-icon icon="check-circle" size="1x" class="verified-icon" />
            <router-link
              v-if="project.approverRef"
              :to="{ name: 'Profile', params: { userId: project.approverRef.id } }"
            >
              Verified by
              {{
                project.approverRef && project.approverRef.username
                  ? project.approverRef.username
                  : 'Mzingo Partners'
              }}
            </router-link>
            <span v-if="!project.approverRef">Verified by Mzingo Partners</span>
          </div>
          <div
            v-if="!project.isVerified && project.ownedByMe"
            class="project__verified-status"
            @click="unverifiedStatusAlarm"
          >
            <font-awesome-icon icon="check-circle" size="2x" class="unverified-icon" />Unverified
            Project
          </div>

          <!-- Project Headline -->
          <div class="project__headline">{{ project.headline }}</div>

          <!-- Give Button -->
          <button
            v-if="!project.ownedByMe"
            class="give-btn small-button"
            @click="scrollToTab('funding')"
          >
            Give
          </button>

          <!-- Edit Project Button -->
          <button
            v-if="project.ownedByMe"
            class="give-btn small-button"
            @click="$router.push({ name: 'EditProject', params: { projectId: project.id } })"
          >
            Edit Project
          </button>

          <!-- Stats Section with follower/funder count & share button -->
          <div class="project__stats">
            <div>
              <div class="project__stats-title">FOLLOWERS</div>
              <div class="project__stats-count">{{ project.followerCount }}</div>
            </div>
            <div>
              <div class="project__stats-title">FUNDERS</div>
              <div class="project__stats-count">{{ project.funderCount }}</div>
            </div>
            <div @click="$modal.show('social-share-modal')">
              <div class="project__stats-title">SHARE</div>
              <font-awesome-icon icon="share" size="1x" class="share-icon" />
            </div>
          </div>

          <!-- Discuss and Follow Buttons -->
          <div v-if="!project.ownedByMe" class="btns-container">
            <button class="small-button small-button--inverted" @click="scrollToTab('discuss')">
              Discuss
            </button>

            <LoadingSpinnerButton
              class="small-button small-button--inverted"
              :loading="loading"
              :text="project.followedByMe ? 'Unfollow' : 'Follow'"
              @click="toggleFollowProject(project.id)"
            />
          </div>

          <!-- START - ALL TABS... -->
          <div id="tabs">
            <!-- 'Activator Comments' Tab -->
            <div v-if="showActivatorCommentsTab" class="tab">
              <div class="tab__header" @click="toggleOpenTab('activator-comments')">
                Activator Comments
                <font-awesome-icon
                  icon="chevron-down"
                  size="1x"
                  class="arrow"
                  :class="{ 'arrow--reversed': openedTabs.includes('activator-comments') }"
                />
              </div>
              <TransitionExpand>
                <div v-if="openedTabs.includes('activator-comments')">
                  <div class="tab__content">
                    {{
                      project.activatorNotes.length
                        ? project.activatorNotes
                        : 'You have no activator comments to review.'
                    }}
                  </div>
                  <button
                    v-if="project.activatorNotes.length && project.ownedByMe"
                    class="medium-button action-btn"
                    @click="toggleEditActivatorNotes"
                  >
                    Edit Activator Notes
                  </button>

                  <div v-if="showEditActivatorNotes">
                    <div style="padding: 0 1rem;">
                      <textarea
                        v-model="editedActivatorNotes"
                        class="textarea"
                        name="showEditActivatorNotes"
                        cols="30"
                        rows="10"
                      ></textarea>
                    </div>
                    <div>
                      <button class="small-button" @click="toggleEditActivatorNotes">Cancel</button>
                      <button class="small-button" @click="submitNewActivatorNotes">Save</button>
                    </div>
                  </div>
                </div>
              </TransitionExpand>
            </div>

            <!-- 'Funding' Tab -->
            <div id="funding" class="tab">
              <div class="tab__header" @click="toggleOpenTab('funding')">
                Funding
                <font-awesome-icon
                  icon="chevron-down"
                  size="1x"
                  class="arrow"
                  :class="{ 'arrow--reversed': openedTabs.includes('funding') }"
                />
              </div>
              <TransitionExpand>
                <div v-if="openedTabs.includes('funding')">
                  <div
                    v-if="projectGoals.list.length === 0 && !project.ownedByMe"
                    class="tab__content"
                  >
                    There are currently no funding goals for this project. Check back later!
                  </div>
                  <div
                    v-if="projectGoals.list.length === 0 && project.ownedByMe"
                    class="tab__content"
                  >
                    You have no funding goals for this project. Click below to create one!
                  </div>
                  <router-link
                    v-if="projectGoals.list.length === 0 && project.ownedByMe"
                    :to="{ name: 'NewProjectGoals', params: { projectId: project.id } }"
                  >
                    <button class="small-button action-btn">Add Goal</button>
                  </router-link>
                  <GoalCard
                    v-for="goal in projectGoals.list"
                    :key="goal.id"
                    style="margin: 0; border: 0;"
                    :hide-all-goals="true"
                    :goal="goal"
                    :owned-by-me="project.ownedByMe"
                    :project="project"
                  />
                  <router-link
                    v-if="projectGoals.list.length > 0"
                    :to="{ name: 'GoalList', params: { projectId: project.id } }"
                    class="link"
                    >See More</router-link
                  >
                </div>
              </TransitionExpand>
            </div>

            <!-- 'Problem/Solution' Tab -->
            <div class="tab">
              <div class="tab__header" @click="toggleOpenTab('problem')">
                Problem/Solution
                <font-awesome-icon
                  icon="chevron-down"
                  size="1x"
                  class="arrow"
                  :class="{ 'arrow--reversed': openedTabs.includes('problem') }"
                />
              </div>
              <TransitionExpand>
                <div v-if="openedTabs.includes('problem')">
                  <div class="tab__content">
                    {{
                      project.shortDescription
                        ? project.shortDescription
                        : 'The project details are currently being edited. Check back later to read up on what this project is about!'
                    }}
                  </div>
                </div>
              </TransitionExpand>
            </div>

            <!-- 'Desired Impact' Tab -->
            <div class="tab">
              <div class="tab__header" @click="toggleOpenTab('impact')">
                Desired Impact
                <font-awesome-icon
                  icon="chevron-down"
                  size="1x"
                  class="arrow"
                  :class="{ 'arrow--reversed': openedTabs.includes('impact') }"
                />
              </div>
              <TransitionExpand>
                <div v-if="openedTabs.includes('impact')">
                  <div class="tab__content">
                    {{
                      project.impact
                        ? project.impact
                        : "Details on the impact this project are currently being edited. Check back later to read more about the impact this project will have in the maker's community."
                    }}
                  </div>
                </div>
              </TransitionExpand>
            </div>

            <!-- 'Updates' Tab -->
            <div class="tab">
              <div class="tab__header" @click="toggleOpenTab('updates')">
                Updates
                <font-awesome-icon
                  icon="chevron-down"
                  size="1x"
                  class="arrow"
                  :class="{ 'arrow--reversed': openedTabs.includes('updates') }"
                />
              </div>
              <TransitionExpand>
                <div v-if="openedTabs.includes('updates')">
                  <div
                    v-if="projectUpdates.list.length === 0 && !project.ownedByMe"
                    class="tab__content"
                  >
                    There are no current updates for this project. Check back later!
                  </div>
                  <UpdateCard
                    v-for="update in projectUpdates.list"
                    :key="update.id"
                    :update="update"
                    :owned-by-me="project.ownedByMe"
                    :project="project"
                    @delete-update="deleteUpdate"
                  />
                  <router-link
                    v-if="project.ownedByMe"
                    :to="{ name: 'NewUpdate', params: { projectId: project.id } }"
                  >
                    <button class="small-button action-btn">Post Update</button>
                  </router-link>
                </div>
              </TransitionExpand>
            </div>

            <!-- 'Discuss' Tab -->
            <div id="discuss" class="tab">
              <div class="tab__header" @click="toggleOpenTab('discuss')">
                Discuss
                <font-awesome-icon
                  icon="chevron-down"
                  size="1x"
                  class="arrow"
                  :class="{ 'arrow--reversed': openedTabs.includes('discuss') }"
                />
              </div>
              <TransitionExpand>
                <div v-if="openedTabs.includes('discuss')">
                  <DiscussionCard
                    v-for="question in projectQuestions.list"
                    :key="question.id"
                    :question="question"
                  />
                  <router-link
                    v-if="project.ownedByMe"
                    :to="{ name: 'Questions', params: { projectId: project.id } }"
                  >
                    <button class="small-button action-btn">Answer Questions</button>
                  </router-link>

                  <div v-if="!project.ownedByMe">
                    <div v-if="projectQuestions.list.length === 0" class="tab__content">
                      There are currently no topics of discussion for this project. Check back
                      later!
                    </div>
                    <button
                      v-if="userIsLoggedIn"
                      class="small-button action-btn"
                      @click="toggleAskQuestionBox"
                    >
                      Ask a Question
                    </button>
                    <div v-if="showAskQuestionBox">
                      <div style="padding: 0 1rem;">
                        <textarea
                          id
                          v-model="questionBeingAsked"
                          class="textarea"
                          name="textarea"
                          cols="30"
                          rows="5"
                        ></textarea>
                      </div>
                      <LoadingSpinnerButton
                        class="small-button submit-question-btn"
                        :loading="loading"
                        text="Submit Question"
                        @click="submitQuestion"
                      />
                    </div>
                  </div>
                </div>
              </TransitionExpand>
            </div>
          </div>
          <!-- END - ALL TABS... -->

          <div v-if="!userIsLoggedIn" class="project__footer">
            <div class="footer-text">Interested in staying up to date with this project?</div>
            <router-link :to="{ name: 'UserOnboarding' }">
              <div class="footer-subtext" @click="sendCreateProfileEventGA">
                Join the Mzingo Partners Community today
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <SocialShareModal :project="project" />

    <Spinner v-if="loading" />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import Project from '@/services/projects'
import Goal from '@/services/goals'
import Update from '@/services/updates'
import Question from '@/services/questions'
import User from '@/services/users'
import CollectionManager from '@/services/collectionManager'
import { errorMessage } from '@/services/utils/apiErrors'

import GoalCard from '@/components/GoalCard'
import UpdateCard from '@/components/UpdateCard'
import DiscussionCard from '@/components/DiscussionCard'
import Subheader from '@/components/Subheader'
import LoadingSpinnerButton from '@/components/buttons/LoadingSpinnerButton'
import ProjectProfileImage from '@/components/images/ProjectProfileImage'
import UserProfileImage from '@/components/images/UserProfileImage'
import SocialShareModal from '@/components/modals/SocialShareModal'
import Spinner from '@/components/Spinner'
import TransitionExpand from '@/components/TransitionExpand'

/** Redesigned Project page based on new mockups (Nov 2019). */
export default {
  name: 'Project',
  components: {
    GoalCard,
    UpdateCard,
    DiscussionCard,
    Subheader,
    LoadingSpinnerButton,
    ProjectProfileImage,
    UserProfileImage,
    SocialShareModal,
    Spinner,
    TransitionExpand,
  },
  data() {
    return {
      project: Project.create(),
      projectGoals: CollectionManager.create({
        ModelClass: Goal,
        filters: { project: null, future: false },
      }),
      projectUpdates: CollectionManager.create({
        ModelClass: Update,
        filters: { project: null },
      }),
      projectQuestions: CollectionManager.create({
        ModelClass: Question,
        filters: { project: null, answered: true },
      }),
      showEditActivatorNotes: false,
      editedActivatorNotes: '',
      showAskQuestionBox: false,
      questionBeingAsked: '',
      openedTabs: ['funding'],
      loading: false,
    }
  },
  computed: {
    ...mapState(['user', 'token']),
    ...mapGetters(['userIsLoggedIn']),
    userNotAllowed() {
      /* User is not allowed to see project if:
       * 1) project is not verified
       * 2) and user isn't the Maker
       * 3) and user isn't the Activator of the Maker of the project
       * 4) and user isn't a staff
       */
      return (
        !this.project.isVerified &&
        !this.project.ownedByMe &&
        !(this.project.activator == this.user.id) &&
        !this.user.isStaff
      )
    },
    showActivatorCommentsTab() {
      if (this.userIsLoggedIn) {
        if (this.project.ownedByMe) {
          return true
        }
        return this.project.activator == this.user.id
      }
      return false
    },
  },
  async created() {
    this.project.id = this.$route.params.projectId
    this.projectGoals.filters.project = this.project.id
    this.projectUpdates.filters.project = this.project.id
    this.projectQuestions.filters.project = this.project.id
    await this.refreshProjectData()

    if (this.userNotAllowed) {
      this.$router.replace({ name: 'Error404' })
    }
  },
  methods: {
    async refreshProjectData() {
      this.loading = true
      try {
        // Refresh Project
        const projectResponse = await Project.api.retrieve(this.project.id)
        this.project = projectResponse
        // Refresh Project Goals
        await this.projectGoals.refresh()
        // Refresh Project Updates
        await this.projectUpdates.refresh()
        // Refresh Project Questions
        await this.projectQuestions.refresh()
      } catch {
        errorMessage()
      } finally {
        this.loading = false
      }
    },
    unverifiedStatusAlarm() {
      this.$Alert.alert({
        message: `
          <h3>Project Status - Unverified</h3>
          <p>Your project is still in our review process! If you haven't heard from your activator more than 3 days after submitting your project, reach out to mzingopartners@gmail.com. See notes from your activator at the top of your project detail page.</p>
          `,
        timeout: 5000,
      })
    },
    toggleEditActivatorNotes() {
      if (!this.showEditActivatorNotes) {
        this.editedActivatorNotes = this.project.activatorNotes
      }
      this.showEditActivatorNotes = !this.showEditActivatorNotes
    },
    async submitNewActivatorNotes() {
      this.loading = true
      try {
        const fields = ['activatorNotes']
        const newProject = Project.create(this.project)
        newProject['activatorNotes'] = this.editedActivatorNotes
        const projectResponse = await Project.api.update(newProject, fields)
        this.project = projectResponse
        this.$Alert.alert({
          type: 'success',
          message: `<h2>Activator Notes Updated</h2>`,
          timeout: 3000,
        })
        this.toggleEditActivatorNotes()
      } catch {
        errorMessage()
      } finally {
        this.loading = false
      }
    },
    toggleOpenTab(selectedTab) {
      if (this.loading) {
        return
      }

      if (this.openedTabs.includes(selectedTab)) {
        this.openedTabs = this.openedTabs.filter(tab => tab !== selectedTab)
      } else {
        this.openedTabs.push(selectedTab)
      }
    },
    scrollToTab(selectedTab) {
      if (!this.openedTabs.includes(selectedTab)) {
        this.toggleOpenTab(selectedTab)
      }
      let element = document.getElementById(selectedTab)
      element.scrollIntoView({ behavior: 'smooth' })
    },
    async toggleFollowProject(projectId) {
      if (!this.userIsLoggedIn) {
        this.$router.push({ name: 'Login', query: { projectId: this.project.id } })
        return
      }

      try {
        const fields = ['followingProjects']
        let newUser = User.create(this.user)
        if (this.project.followedByMe) {
          newUser.followingProjects = this.user.followingProjects.filter(p => p.id === projectId)
        } else {
          newUser.followingProjects = this.user.followingProjects
          newUser.followingProjects.push(projectId)
        }
        await User.api.update(newUser, fields)
        await this.refreshProjectData()
      } catch {
        errorMessage()
      }
    },
    async deleteUpdate(update) {
      try {
        await Update.api.delete(update)
        this.$Alert.alert({
          type: 'success',
          message: `<h2>Update Deleted</h2>`,
          timeout: 3000,
        })
        this.refreshProjectData()
      } catch {
        errorMessage()
      }
    },
    toggleAskQuestionBox() {
      this.showAskQuestionBox = !this.showAskQuestionBox
      this.questionBeingAsked = ''
    },
    async submitQuestion() {
      const newQuestion = Question.create({
        project: this.project.id,
        text: this.questionBeingAsked,
      })

      try {
        this.loading = true

        if (newQuestion.text) {
          await Question.api.create(newQuestion)
          this.toggleAskQuestionBox()
          this.refreshProjectData()
          this.$Alert.alert({
            type: 'success',
            message: `<h2>Question Submitted! Check back here for your answer</h2>`,
            timeout: 3000,
          })
        } else {
          this.$Alert.alert({
            type: 'error',
            message: `<h2>Write a question to be submitted!</h2>`,
            timeout: 2000,
          })
        }
      } catch {
        errorMessage()
      } finally {
        this.loading = false
      }
    },
    /** Google Analytics - Send "Start Creating Profile" event to GA. */
    sendCreateProfileEventGA() {
      this.$ga.event({
        eventCategory: 'Start Creating Profile',
        eventAction: 'From a Project Page',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';
@import '../styles/layout';
@import '../styles/forms';
@import '../styles/mixins/buttons';

.project {
  text-align: center;

  &__subheader {
    position: absolute;
    width: 100%;
  }

  &__image {
    padding-top: 2rem;
  }

  &__card {
    display: inline-block;
    width: 90%;
    margin-top: -60px;
    margin-bottom: 40px;
    border-radius: 8px;
    box-shadow: 0 20px 20px 0 rgba(0, 0, 0, 0.08);
    background-color: white;
  }

  &__username {
    height: 22px;
    font-size: 18px;
    font-weight: 900;
    margin: 16px auto;
    color: black;
  }

  &__verified-status {
    font-size: 10px;
    line-height: normal;
    color: $gray;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__headline {
    width: 300px;
    font-size: 14px;
    line-height: 1.2rem;
    color: #000000;
    margin: 26px auto;
  }

  &__stats {
    display: flex;
    justify-content: space-evenly;
    height: 70px;
    background-color: #f8f8f8;
    margin-top: 26px;
  }
  &__stats-title {
    text-align: center;
    font-size: 10px;
    margin-top: 16px;
    margin-bottom: 12px;
    width: 61px;
    color: #9b9b9b;
  }
  &__stats-count {
    font-size: 16px;
    font-weight: 600;
    font-stretch: normal;
    color: #262626;
  }

  &__footer {
    height: 68px;
    background-color: rgba(234, 149, 47, 0.16);
    border-radius: 0 0 8px 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .footer-text {
      font-size: 12px;
      margin-bottom: 10px;
    }

    .footer-subtext {
      font-size: 10px;
      color: $primary;
    }
  }
}

.unverified-icon {
  margin-right: 0.3rem;
}

.verified-icon {
  color: $primary;
  margin-right: 0.3rem;
}

.share-icon {
  cursor: pointer;
  font-size: 20px;
  margin-top: -5px;
}

.link {
  color: $primary;
  font-size: 14px;
  display: flex;
  padding: 0 0 14px 14px;
}

.give-btn {
  margin: 0;
}

.btns-container {
  display: flex;
  justify-content: center;
  margin: 5px 0;
}

.action-btn {
  width: 10rem;
  margin-top: 0;
}

.submit-question-btn {
  display: block;
  margin: 1rem auto;
}

.tab {
  border-top: solid 1px #f5f5f5;
  user-select: none;

  &__header {
    display: flex;
    justify-content: space-between;
    font-size: 18px;
    padding: 18px 14px;
    cursor: pointer;
  }

  &__content {
    text-align: left;
    padding: 4px 20px 28px 14px;
    font-size: 14px;
    line-height: 1.2rem;
  }
  .arrow {
    width: 7px;
    height: 20px;
    &--reversed {
      transform: rotate(180deg);
    }
  }
}
</style>
